import { Alert } from "@msuite/ui/alert";
import { Combobox, InlineSelect } from "@msuite/ui/form";
import { useTranslation } from "@msuite/ui/localization";
import type { CrewDefinitionFormSchema } from "@msuite/utils/schemas";
import { guardString } from "@msuite/utils/strings";
import React, { useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useCrewDefinitions } from "@/hooks/use-crew-definitions";
import { useMembers } from "@/hooks/use-members";
import { findStaffingConflicts } from "./staffing-conflicts";

/** Props Interface */
interface CrewDefinitionFormStaffingProps {
	crewDefinitionId?: string;
}

/**
 * Who is on the crew. Only members whose teams carry `crew-scheduling` are offered, which is the
 * same rule the handler refuses a write on — the picker is the courtesy and the handler is the
 * guarantee.
 */
export function CrewDefinitionFormStaffing({ crewDefinitionId }: CrewDefinitionFormStaffingProps) {
	/** Hooks */
	const { t } = useTranslation();
	const methods = useFormContext<CrewDefinitionFormSchema>();

	/** Query */
	const { members } = useMembers({ capabilities: ["crew-scheduling"] });
	const { crewDefinitions } = useCrewDefinitions();

	/** State */
	const leadUserId = useWatch({ control: methods.control, name: "leadUserId" });
	const memberUserIds = useWatch({ control: methods.control, name: "memberUserIds" });
	const startDate = useWatch({ control: methods.control, name: "startDate" });
	const endDate = useWatch({ control: methods.control, name: "endDate" });

	/** Computed */
	const memberOptions = useMemo(() => {
		const options: Array<{ label: string; value: string }> = [];
		for (const member of members) {
			// The foreman has a field of their own, so offering them here would let the same person
			// onto the crew twice — which `unique_member_per_crew_definition` refuses outright.
			if (member.user.id === leadUserId) continue;
			options.push({ label: guardString(member.user.name), value: member.user.id });
		}
		return options;
	}, [members, leadUserId]);

	const leadOptions = useMemo(
		() =>
			members.map((member) => ({
				label: guardString(member.user.name),
				value: member.user.id,
			})),
		[members],
	);

	const conflicts = useMemo(() => {
		const userIds = [...(memberUserIds ?? [])];
		if (leadUserId) userIds.push(leadUserId);
		return findStaffingConflicts(crewDefinitions, {
			crewDefinitionId: crewDefinitionId ?? null,
			startDate,
			endDate: endDate ?? null,
			userIds,
		});
	}, [crewDefinitions, crewDefinitionId, startDate, endDate, memberUserIds, leadUserId]);

	const conflictDescription = useMemo(() => {
		const lines: string[] = [];
		for (const conflict of conflicts) {
			const name = getMemberName(members, conflict.userId);
			lines.push(
				t("planning.crews.staffing_conflict_line", {
					user: guardString(name),
					crew: guardString(conflict.crewName),
					from: conflict.overlapStart,
					to: guardString(conflict.overlapEnd, "∞"),
				}),
			);
		}
		return lines.join("\n");
	}, [conflicts, members, t]);

	/** Render */
	return (
		<React.Fragment>
			<Combobox
				control={methods.control}
				name="leadUserId"
				label={t("planning.crews.crew_lead")}
				placeholder={t("users.select_user")}
				isRequired={false}
				isNullable
				keyExtractor={(item) => item.value}
				displayValue={(item) => guardString(item.label)}
				options={leadOptions}
			/>
			<InlineSelect
				control={methods.control}
				name="memberUserIds"
				label={t("planning.crews.crew_members")}
				isRequired={false}
				options={memberOptions}
			/>
			{/* A warning and not a rule: a person genuinely can move between crews mid-span. What
			    this prevents is the silent version, where the second crew is planned without anyone
			    noticing the first still has them. */}
			{conflicts.length > 0 && (
				<Alert
					variant="warning"
					title={t("planning.crews.staffing_conflict_title")}
					description={conflictDescription}
				/>
			)}
		</React.Fragment>
	);
}

/** The name behind a conflicting member's id, or null when the list no longer offers them. */
function getMemberName(
	members: ReturnType<typeof useMembers>["members"],
	userId: string,
): string | null {
	for (const member of members) {
		if (member.user.id === userId) return member.user.name;
	}
	return null;
}
